import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { LegalPageShell } from '@/components/LegalPageShell'
import { getLegalDocumentsApi } from '@/lib/api'

export const Route = createFileRoute('/dpa')({
  component: DpaPage,
})

function DpaPage() {
  const [dpa, setDpa] = useState<{ version: string; content: string } | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    getLegalDocumentsApi()
      .then((res) => {
        if (!cancelled) setDpa(res.dpa)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // The canonical text is a title line ("PEERPASS DATA PROCESSING AGREEMENT — vX.Y")
  // followed by the numbered clauses. Drop the title and render each clause verbatim.
  const clauses = dpa
    ? dpa.content.split('\n').map((line) => line.trim()).filter(Boolean).slice(1)
    : []

  return (
    <LegalPageShell title="Data Processing Agreement" updated="September 17, 2026">
      <section className="space-y-4">
        <p className="text-sm leading-relaxed text-white/60">
          This Data Processing Agreement (&ldquo;DPA&rdquo;) governs how PeerPass processes the
          personal data you submit &mdash; employee names, contact details, banking information and
          payment records &mdash; on your organisation&rsquo;s behalf. Your organisation is the Data
          Controller; PeerPass acts as the Data Processor.
        </p>
        {dpa && (
          <p className="text-xs font-medium uppercase tracking-wider text-[#00E5A0]">
            Version {dpa.version}
          </p>
        )}
      </section>

      {failed && (
        <p className="text-sm leading-relaxed text-white/60">
          The Data Processing Agreement could not be loaded. Please try again, or{' '}
          <a href="/contact" className="text-[#00E5A0] hover:underline">
            contact us
          </a>.
        </p>
      )}

      {!dpa && !failed && <p className="text-sm text-white/40">Loading&hellip;</p>}

      {dpa && (
        <section className="space-y-4">
          {clauses.map((clause, index) => (
            <p key={index} className="text-sm leading-relaxed text-white/60">
              {clause}
            </p>
          ))}
        </section>
      )}

      <section className="space-y-4">
        <p className="text-sm leading-relaxed text-white/60">
          The binding DPA &mdash; with versioning and electronic signature &mdash; is executed
          in-app by an authorised signatory. Where this page differs from the signed document, the
          signed document controls.
        </p>
      </section>
    </LegalPageShell>
  )
}